import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { TrustLogos } from "@/components/site/TrustLogos";
import { Cases } from "@/components/site/Cases";
import { DemoCTA } from "@/components/site/DemoCTA";
import { useT } from "@/i18n/useLocale";

import { HeroFacturas } from "@/components/facturas/HeroFacturas";
import { FlujoFacturas } from "@/components/facturas/FlujoFacturas";
import { ValidacionVisual } from "@/components/facturas/ValidacionVisual";
import { MetricasFacturas } from "@/components/facturas/MetricasFacturas";
import { SeguridadFacturas } from "@/components/facturas/SeguridadFacturas";
import { FAQFacturas, faqs as faqFacturas } from "@/components/facturas/FAQFacturas";
import { OG_IMAGE_META } from "@/lib/ogImage";
import { stripBold } from "@/components/site/Rich";
import { dict } from "@/i18n/dict";

const TITLE = "Lectura y Carga Automática de Facturas con IA — DigitAI | The Eye";
const DESCRIPTION =
  "Lectura de facturas con IA, constatación de CAE en ARCA, detección de apócrifas y duplicadas, control contra la OC y carga en SAP, Oracle o Dynamics. Sin carga manual.";
const URL = "https://theeye.io/automatizacion-facturas";

const steps = dict.es.facturas.flujo.steps;

export const Route = createFileRoute("/automatizacion-facturas")({
  component: FacturasPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      ...OG_IMAGE_META,
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": `${URL}#service`,
          name: "Automatización de facturas de proveedores con IA",
          alternateName: ["Lectura de facturas con IA", "Carga automática de facturas", "Automatización de cuentas a pagar"],
          provider: { "@type": "Organization", name: "The Eye", alternateName: "TheEye", url: "https://theeye.io/" },
          brand: { "@type": "Brand", name: "DigitAI" },
          description: DESCRIPTION,
          url: URL,
          serviceType: "Automatización de cuentas a pagar y procesamiento de facturas con IA",
          category: "Accounts payable automation",
          areaServed: ["AR", "MX", "CO", "CL", "BR", "ES", "UY", "PE"].map((c) => ({ "@type": "Country", name: c })),
          audience: { "@type": "BusinessAudience", audienceType: "CFO, Cuentas a Pagar, Administración y Finanzas" },
          availableLanguage: ["es", "en", "pt"],
          isRelatedTo: [
            { "@type": "Service", name: "Approvals", url: "https://theeye.io/approvals" },
            { "@type": "Service", name: "DigitAI", url: "https://theeye.io/digitai" },
          ],
          knowsAbout: ["ARCA", "Constatación de CAE", "Facturas apócrifas", "Facturas duplicadas", "OCR", "IDP", "3-way match", "SAP", "Oracle", "Microsoft Dynamics 365", "NetSuite", "Odoo", "Finnegans", "Softland"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          totalTime: "P14D",
          name: "Cómo automatizar la lectura y carga de facturas de proveedores con IA",
          description: DESCRIPTION,
          step: steps.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            text: `${stripBold(s.body)} ${s.bullets.map((b) => stripBold(b)).join(". ")}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqFacturas.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          url: URL,
          name: TITLE,
          description: DESCRIPTION,
          abstract: dict.es.facturas.hero.summary,
          inLanguage: "es",
          dateModified: "2026-09-25",
          isPartOf: { "@type": "WebSite", url: "https://theeye.io/", name: "The Eye" },
          about: { "@id": `${URL}#service` },
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".speakable-summary"] },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "The Eye", item: "https://theeye.io/" },
            { "@type": "ListItem", position: 2, name: "DigitAI", item: "https://theeye.io/digitai" },
            { "@type": "ListItem", position: 3, name: "Automatización de facturas", item: URL },
          ],
        }),
      },
    ],
  }),
});

function FacturasPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SmoothScroll />
      <Navbar />
      <main>
        <HeroFacturas />
        <section className="bg-background pt-4 pb-12 lg:pt-6 lg:pb-16">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <TrustLogos />
          </div>
        </section>
        <FlujoFacturas />
        <ValidacionVisual />
        <MetricasFacturas />
        <SeguridadFacturas />
        <Cases />
        <FacturasDemoCTA />
        <FAQFacturas />
      </main>
      <Footer />
    </div>
  );
}

function FacturasDemoCTA() {
  const t = useT();
  const d = t.facturas.demoCta;
  return <DemoCTA eyebrow={d.eyebrow} title={d.title} subtitle={d.subtitle} source="digitai" />;
}
